import React from 'react';
import { PRIVACY_POLICY } from '@shared/constants';

/**
 * @group Legal Components
 *
 * @summary
 * <blockquote>Type-safe component that renders a structured privacy policy into accessible semantic HTML.</blockquote>
 *
 * @example
 * ```typescript
 * import { PrivacyPolicyRenderer } from '@/components/legal';
 * import { PRIVACY_POLICY } from '@shared/constants/legal';
 *
 * export default function PrivacyPage() {
 *   return <PrivacyPolicyRenderer policy={PRIVACY_POLICY} />;
 * }
 * ```
 */

interface PrivacyPolicyRendererProps {
  /** The structured privacy policy object to render */
  policy: typeof PRIVACY_POLICY;
  /** Optional CSS class for styling */
  className?: string;
}

/**
 * Renders a comprehensive privacy policy document with semantic HTML.
 * Includes table of contents with anchor links, proper heading hierarchy,
 * and accessibility features like ARIA labels.
 */
export function PrivacyPolicyRenderer({
  policy,
  className = '',
}: PrivacyPolicyRendererProps) {
  return (
    <article className={`privacy-policy-container ${className}`}>
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-4xl font-bold mb-2">{policy.metadata.title}</h1>
        <p className="text-gray-600">Last updated: {policy.metadata.lastUpdated}</p>
      </header>

      {/* Table of Contents */}
      <nav aria-label="Table of Contents" className="mb-8 p-6 bg-gray-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Table of Contents</h2>
        <ul className="space-y-2">
          {policy.sections.map((section, idx) => (
            <li key={idx}>
              <a
                href={`#section-${idx}`}
                className="text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                {section.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Main Content */}
      <main className="space-y-8">
        {/* Introduction */}
        <section id="introduction" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
          {policy.introduction.map((paragraph, idx) => (
            <p key={idx} className="text-base leading-relaxed mb-4">
              {paragraph}
            </p>
          ))}
        </section>

        {/* Sections */}
        {policy.sections.map((section, idx) => (
          <section key={idx} id={`section-${idx}`} className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{section.title}</h2>
            <ul className="list-disc list-inside space-y-2">
              {section.content.map((item, itemIdx) => (
                <li key={itemIdx} className="text-base text-gray-700">
                  {item}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </main>
    </article>
  );
}
